/**
 * @file Theme preference.
 *
 * This is the one thing kept in localStorage rather than IndexedDB: it must be
 * readable synchronously before first paint to avoid a flash of the wrong
 * theme, which an async IndexedDB read cannot do.
 */

/** Storage key, also read by the inline script in index.html. */
export const THEME_STORAGE_KEY = 'itf-theme';

/** Themes the user can choose between. */
export type Theme = 'light' | 'dark';

/**
 * Reads the stored theme preference.
 *
 * @returns The stored theme, or undefined to follow the system setting.
 */
export function readStoredTheme(): Theme | undefined {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reports the theme currently applied to the document.
 *
 * @returns The active theme.
 */
export function currentTheme(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * Applies a theme and remembers it. Storage failures are non-fatal: the theme
 * still applies for this page view.
 *
 * @param theme Theme to apply.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* Private mode or blocked storage; the class is already applied. */
  }
}
